import { config } from '../../config/environment';
import type { ApiResponse, ApiError } from '../types';
import { useAuthStore } from '../../stores/authStore';

interface StandardApiEnvelope<T> {
  success: boolean;
  httpStatus?: number;
  code?: string;
  message?: string;
  userMessage?: string;
  category?: string;
  uiHint?: string;
  severity?: string;
  retryable?: boolean;
  fieldErrors?: Record<string, string[]> | null;
  traceId?: string | null;
  data?: T;
  errors?: string[];
  error?: string;
  statusCode?: number;
}

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
}

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;

  private handleUnauthorizedResponse = (): void => {
    useAuthStore.getState().logout();
  };

  constructor(baseURL?: string) {
    this.baseURL = baseURL ?? config.api.baseURL;
    this.timeout = config.api.timeout;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  // Método genérico mejorado para hacer peticiones HTTP
  private async request<T>(
    endpoint: string,
    options: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const {
      method = 'GET',
      headers = {},
      body,
      timeout = this.timeout,
    } = options;

    try {
      // Configurar headers con autenticación
      const requestHeaders = {
        ...this.defaultHeaders,
        ...headers,
      };

      // Agregar body solo para métodos que lo requieren
      const executeRequest = async (): Promise<{ response: Response; envelope: StandardApiEnvelope<T> | null }> => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const requestOptions: RequestInit = {
          method,
          headers: requestHeaders,
          signal: controller.signal,
          credentials: 'include',
        };

        if (body && method !== 'GET') {
          requestOptions.body = JSON.stringify(body);
        }

        try {
          const response = await fetch(url, requestOptions);
          const envelope = await this.parseResponseBody<T>(response);
          return { response, envelope };
        } finally {
          clearTimeout(timeoutId);
        }
      };

      let { response, envelope } = await executeRequest();
      const normalizedStatus =
        envelope?.httpStatus || envelope?.statusCode || response.status;

      // Error HTTP o error lógico reportado por la API
      if (!response.ok || envelope?.success === false) {
        const shouldAttemptRefresh =
          normalizedStatus === 401 &&
          !endpoint.includes('/auth/login') &&
          !endpoint.includes('/auth/refresh') &&
          !endpoint.includes('/auth/logout') &&
          (envelope?.code === 'AUTH_TOKEN_INVALID' ||
            envelope?.code === 'AUTH_SESSION_INVALID' ||
            envelope?.code === 'AUTH_UNAUTHORIZED' ||
            envelope?.code === 'AUTH_TOKEN_EXPIRED');

        if (shouldAttemptRefresh) {
          const refreshed = await this.tryRefreshSession();
          if (refreshed) {
            const retried = await executeRequest();
            response = retried.response;
            envelope = retried.envelope;

            const retriedStatus =
              envelope?.httpStatus || envelope?.statusCode || response.status;

            if (response.ok && envelope?.success !== false) {
              return {
                data: (envelope?.data ?? (null as T)) as T,
                success: true,
                status: retriedStatus,
                statusCode: envelope?.statusCode || retriedStatus,
                httpStatus: envelope?.httpStatus || retriedStatus,
                message: envelope?.message,
                userMessage: envelope?.userMessage,
                code: envelope?.code,
                category: envelope?.category,
                uiHint: envelope?.uiHint,
                severity: envelope?.severity,
                retryable: envelope?.retryable,
                fieldErrors: envelope?.fieldErrors || null,
                traceId: envelope?.traceId || null,
                errors: envelope?.errors || [],
              };
            }
          }
        }

        if (
          (envelope?.httpStatus || envelope?.statusCode || response.status) === 401 &&
          !endpoint.includes('/auth/login') &&
          (envelope?.code === 'AUTH_TOKEN_INVALID' ||
            envelope?.code === 'AUTH_SESSION_INVALID' ||
            envelope?.code === 'AUTH_UNAUTHORIZED')
        ) {
          this.handleUnauthorizedResponse();
        }

        const finalStatus = envelope?.httpStatus || envelope?.statusCode || response.status;
        const finalMessage =
          envelope?.userMessage ||
          envelope?.message ||
          envelope?.error ||
          envelope?.errors?.[0] ||
          this.getDefaultMessageByStatus(finalStatus);

        const normalizedErrors = envelope?.errors || [];

        return {
          data: null as T,
          success: false,
          status: finalStatus,
          statusCode: envelope?.statusCode || finalStatus,
          httpStatus: envelope?.httpStatus || finalStatus,
          code: envelope?.code,
          message: envelope?.message,
          userMessage: envelope?.userMessage,
          category: envelope?.category,
          uiHint: envelope?.uiHint,
          severity: envelope?.severity,
          retryable: envelope?.retryable,
          fieldErrors: envelope?.fieldErrors || null,
          traceId: envelope?.traceId || null,
          errors: normalizedErrors,
          error: finalMessage,
        };
      }

      // Éxito estandarizado: usar envelope.data como payload de negocio
      return {
        data: (envelope?.data ?? (null as T)) as T,
        success: true,
        status: normalizedStatus,
        statusCode: envelope?.statusCode || normalizedStatus,
        httpStatus: envelope?.httpStatus || normalizedStatus,
        message: envelope?.message,
        userMessage: envelope?.userMessage,
        code: envelope?.code,
        category: envelope?.category,
        uiHint: envelope?.uiHint,
        severity: envelope?.severity,
        retryable: envelope?.retryable,
        fieldErrors: envelope?.fieldErrors || null,
        traceId: envelope?.traceId || null,
        errors: envelope?.errors || [],
      };
    } catch (error) {
      // Manejar errores específicos
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          data: null as T,
          success: false,
          status: 0,
          errors: ['Error de conexión. Verifica tu conexión a internet.'],
          error: 'Error de conexión. Verifica tu conexión a internet.',
        };
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        return {
          data: null as T,
          success: false,
          status: 408,
          errors: ['La petición tardó demasiado en responder.'],
          error: 'La petición tardó demasiado en responder.',
        };
      }

      const errorMessage =
        error instanceof Error ? error.message : 'Error desconocido';
      return {
        data: null as T,
        success: false,
        status: 500,
        errors: [errorMessage],
        error: errorMessage,
      };
    }
  }

  private async tryRefreshSession(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  private async parseResponseBody<T>(
    response: Response
  ): Promise<StandardApiEnvelope<T> | null> {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return null;
    }

    try {
      const parsed = await response.json();
      if (parsed && typeof parsed === 'object') {
        return parsed as StandardApiEnvelope<T>;
      }
      return null;
    } catch {
      return null;
    }
  }

  private getDefaultMessageByStatus(status: number): string {
    switch (status) {
      case 400:
        return 'Solicitud inválida';
      case 401:
        return 'No autenticado o token inválido';
      case 403:
        return 'No tenés permisos para realizar esta acción';
      case 404:
        return 'Recurso no encontrado';
      case 409:
        return 'Conflicto de negocio';
      case 422:
        return 'Error de validación';
      case 500:
        return 'Error interno del servidor';
      default:
        return `HTTP ${status}`;
    }
  }

  // Métodos HTTP básicos mejorados
  async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
      headers,
    });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data,
      headers,
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data,
      headers,
    });
  }

  async delete<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers,
    });
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data,
      headers,
    });
  }
}

export { ApiClient };
export const apiClient = new ApiClient();
